import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { CheckCircle2, XCircle, Info, X } from "lucide-react";

type ToastKind = "success" | "error" | "info";
interface ToastItem { id: number; kind: ToastKind; title: string; description?: string }
const Ctx = createContext<((t: Omit<ToastItem, "id">) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, "id">) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs.slice(-3), { ...t, id }]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), t.kind === "error" ? 8000 : 4500);
  }, []);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-[min(380px,calc(100vw-32px))] flex-col gap-2" role="region" aria-label="Notifications" aria-live="polite">
        {items.map((t) => {
          const Icon = t.kind === "success" ? CheckCircle2 : t.kind === "error" ? XCircle : Info;
          const color = t.kind === "success" ? "var(--good)" : t.kind === "error" ? "var(--critical)" : "var(--info)";
          return (
            <div key={t.id} className="pointer-events-auto flex items-start gap-3 rounded-lg border border-border-strong bg-panel-2 p-3 shadow-xl" role={t.kind === "error" ? "alert" : "status"}>
              <Icon className="mt-0.5 size-4 shrink-0" style={{ color }} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-fg">{t.title}</div>
                {t.description && <div className="mt-0.5 text-xs text-fg-2">{t.description}</div>}
              </div>
              <button className="text-fg-3 hover:text-fg" aria-label="Dismiss" onClick={() => setItems((xs) => xs.filter((x) => x.id !== t.id))}>
                <X className="size-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </Ctx.Provider>
  );
}

export function useToast() {
  const push = useContext(Ctx);
  if (!push) throw new Error("useToast outside ToastProvider");
  return {
    success: (title: string, description?: string) => push({ kind: "success", title, description }),
    error: (title: string, description?: string) => push({ kind: "error", title, description }),
    info: (title: string, description?: string) => push({ kind: "info", title, description }),
  };
}
